/**
 * @module domain/arrows
 * @description Send Love Live Legend Arrow catalog and the fewest-arrows
 * breakdown (vision doc §6). Pure: no React, no I/O.
 *
 * Currency rule (vision §7): the donor always gives in US dollars. A Legend is
 * the THEME of an arrow, named after the Jamaican note or coin with the same
 * number, never the unit. Every face reads "US$20 · Garvey Legend".
 */

/** One donation unit. */
export interface LegendArrow {
    id: string;
    faceUsd: number;
    /** Short name on the arrow face, e.g. "Garvey". */
    name: string;
    /** Full names of the Legend(s) on the source note/coin. */
    legends: string[];
    /** The Jamaican denomination the arrow is named after. */
    source: string;
    /** "rebel" = earned by monthly donors, never purchasable (vision §9). */
    availability: "standard" | "rebel";
}

/**
 * Catalog, verified 2026-10-27 (vision §6.1 + §17 sources). US$2,000 and
 * US$5,000 are disabled pending decision D5 and are therefore not listed.
 */
export const LEGEND_ARROWS: readonly LegendArrow[] = [
    { id: "bustamante-1", faceUsd: 1, name: "Bustamante", legends: ["Sir Alexander Bustamante"], source: "J$1 coin", availability: "standard" },
    { id: "bogle-rebel-2", faceUsd: 2, name: "Bogle Rebel", legends: ["Paul Bogle"], source: "J$2 note (historical)", availability: "rebel" },
    { id: "manley-5", faceUsd: 5, name: "Norman Manley", legends: ["Norman Manley"], source: "J$5 coin", availability: "standard" },
    { id: "gordon-10", faceUsd: 10, name: "Gordon", legends: ["George William Gordon"], source: "J$10 coin", availability: "standard" },
    { id: "garvey-20", faceUsd: 20, name: "Garvey", legends: ["Marcus Garvey"], source: "J$20 coin", availability: "standard" },
    { id: "bogle-gordon-50", faceUsd: 50, name: "Bogle + Gordon", legends: ["Paul Bogle", "George William Gordon"], source: "J$50 note", availability: "standard" },
    { id: "garvey-gold-100", faceUsd: 100, name: "Garvey Gold", legends: ["Marcus Garvey"], source: "J$100 note", availability: "standard" },
    { id: "nanny-sharpe-500", faceUsd: 500, name: "Nanny + Sharpe", legends: ["Nanny of the Maroons", "Sam Sharpe"], source: "J$500 note", availability: "standard" },
    { id: "bustamante-manley-1000", faceUsd: 1000, name: "Bustamante + Manley", legends: ["Sir Alexander Bustamante", "Norman Manley"], source: "J$1,000 note", availability: "standard" },
];

/** @returns true when the source note carries two portraits (plays the doubled animation). */
export function isTwin(arrow: LegendArrow): boolean {
    return arrow.legends.length > 1;
}

/** @returns the arrows a donor can shoot (rebel only when unlocked). */
export function availableArrows(rebelUnlocked: boolean): LegendArrow[] {
    return LEGEND_ARROWS.filter((a: LegendArrow) => a.availability === "standard" || rebelUnlocked);
}

/** @returns the arrow face label, e.g. "US$20 · Garvey Legend". */
export function arrowLabel(arrow: LegendArrow): string {
    return `US$${arrow.faceUsd.toLocaleString("en-US")} · ${arrow.name} Legend`;
}

/**
 * Fewest arrows for a whole-dollar amount, greedy largest-first (vision §6.2:
 * greedy is optimal for this 1-2-5 set; proof script to be saved as
 * libexec/verify-arrow-breakdown.py).
 * @returns arrows, largest first; [] for amounts < 1.
 */
export function breakdown(amountUsd: number, arrows: readonly LegendArrow[]): LegendArrow[] {
    const sorted: LegendArrow[] = [...arrows].sort((a, b) => b.faceUsd - a.faceUsd);
    const out: LegendArrow[] = [];
    let rest: number = Math.floor(amountUsd);
    for (const arrow of sorted) {
        while (rest >= arrow.faceUsd) {
            out.push(arrow);
            rest -= arrow.faceUsd;
        }
    }
    return out;
}

/** Arrows grouped for display: [arrow, count], largest first (e.g. Bustamante ×2). */
export function groupArrows(list: readonly LegendArrow[]): [LegendArrow, number][] {
    const counts: Map<string, [LegendArrow, number]> = new Map();
    for (const a of list) counts.set(a.id, [a, (counts.get(a.id)?.[1] ?? 0) + 1]);
    return [...counts.values()];
}
